'use client'

import { InputProps } from '@headlessui/react'
import clsx from 'clsx'
import Icon from 'lib/components/Icon/Icon'
import { IconNames } from 'lib/settings/icons'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { SetTimeoutReturn } from 'lib/types/Timeout'
import {
  ChangeEvent,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

interface TextInputStandaloneProps
  extends InputAddonProps,
    FormFieldCommonProps,
    Omit<InputProps, 'onChange' | 'className' | 'defaultValue'> {
  /**
   * Input placeholder text
   */
  placeholder?: string
  /**
   * Callback fired when input value changes
   */
  onChange?: (data?: string | number, fieldName?: string) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void

  /**
   * Optional icon to appear in the left side of the text input
   */
  leadingIcon?: IconNames
  /**
   * Provides additional className for the leadingIcon
   */
  leadingIconClassName?: string
  /**
   * Optional icon to appear at the end of the text
   */
  trailingIcon?: IconNames
  /**
   * Provides additional className for the trailingIcon
   */
  trailingIconClassName?: string
  /**
   * Style variant for conditional edit
   */
  conditionalEditStyle?: 'icon'
  /**
   * Optional override for the HTML name attribute on the underlying input.
   * Separate from the react-hook-form registration name — use this to give
   * the input a non-semantic name so browsers cannot heuristically map it
   * to an address/contact autofill field.
   */
  htmlName?: string
}

export interface TextInputProps
  extends ControlledFormField,
    TextInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const TextInputStandalone = forwardRef<
  HTMLInputElement,
  TextInputStandaloneProps
>(function TextInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    className,
    error,
    onChange,
    onBlur,
    defaultValue,
    disabled,
    hint,
    placeholder,
    leadingIcon,
    leadingIconClassName,
    trailingIcon,
    trailingIconClassName,
    conditionalEditStyle,
    maxLength,
    autoComplete,
    htmlName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    value: _value, // excluded — component is uncontrolled; value from RHF field spread must not reach <input> alongside defaultValue
    // InputAddon-specific props — extracted so the rest can be spread onto <input>
    help,
    conditionalEdit,
    onEdit,
    link,
    modal,
    ...htmlInputProps
  }: TextInputStandaloneProps,
  ref,
) {
  const customRef = useRef<HTMLInputElement | undefined>(undefined)
  const generatedId = useId()
  // When htmlName is set we want a non-semantic id so Chrome can't use it
  // as a heuristic signal to identify the field for autofill.
  const effectiveId = htmlName !== undefined ? generatedId : id ?? name

  const conditionalEditCheck = conditionalEdit
  const inputChangeDebounceTimer = useRef<SetTimeoutReturn | undefined>(
    undefined,
  )

  const [defaultValueCached, setDefaultValueCached] = useState(defaultValue)
  const [isEnabled, setIsEnabled] = useState(
    conditionalEditCheck ? false : !disabled,
  )
  const willShowEditAddon = useMemo(
    () => conditionalEditCheck && conditionalEditStyle !== 'icon',
    [conditionalEditCheck, conditionalEditStyle],
  )
  const willShowEditIcon = useMemo(
    () => !isEnabled && conditionalEditCheck && conditionalEditStyle === 'icon',
    [isEnabled, conditionalEditCheck, conditionalEditStyle],
  )

  const onInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (inputChangeDebounceTimer.current) {
        clearTimeout(inputChangeDebounceTimer.current)
      }

      inputChangeDebounceTimer.current = setTimeout(() => {
        inputChangeDebounceTimer.current = undefined
        onChange?.(value, name)
      }, 300)
    },
    [onChange, name],
  )

  // Handles defaultValue change from the outside
  useEffect(() => {
    if (defaultValue !== defaultValueCached) {
      if (customRef.current) {
        customRef.current.value = defaultValue as string

        if (typeof ref === 'function') {
          ref(customRef.current)
        }
      }

      setDefaultValueCached(defaultValue)
      onChange?.(defaultValue, name)
    }
  }, [defaultValue, defaultValueCached, ref, customRef, onChange, name])

  useImperativeHandle(ref, () => customRef.current as HTMLInputElement)

  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={effectiveId}>{label}</InputLabel>}
        <InputAddon
          {...(help !== undefined ? { help } : {})}
          {...(conditionalEdit !== undefined
            ? { conditionalEdit, onEdit }
            : {})}
          {...(link !== undefined ? { link } : {})}
          {...(modal !== undefined ? { modal } : {})}
          {...(conditionalEditCheck
            ? willShowEditAddon
              ? { onEdit: setIsEnabled }
              : { conditionalEdit: false }
            : {})}
        />
      </div>
      <div
        className={clsx(
          'text-input-content flex items-center w-full rounded-8 my-2 transition-shadow bg-white',
          'has-[:disabled]:bg-neutral-200 has-[:disabled]:shadow-input-disabled has-[:disabled]:cursor-not-allowed',
          'hover:shadow-input-active',
          'has-[:focus]:shadow-input-focus',
          error ? '!shadow-input-error' : 'shadow-input',
        )}
      >
        {leadingIcon ? (
          <Icon
            icon={leadingIcon}
            className={clsx(
              'mr-2 ml-4 [&_+_*]:pl-0 flex-shrink-0 w-4 h-4',
              leadingIconClassName,
            )}
          />
        ) : null}
        {htmlName !== undefined && (
          <input
            type="text"
            name={name}
            defaultValue=""
            readOnly
            hidden
            aria-hidden="true"
            tabIndex={-1}
          />
        )}
        <input
          {...(htmlInputProps as React.InputHTMLAttributes<HTMLInputElement>)}
          className={clsx(
            'flex-1 rounded-8 type-body-8 text-black h-12 flex items-center px-4 lg:h-14 bg-transparent w-full',
            'disabled:text-neutral-600 disabled:cursor-not-allowed',
            'focus:outline-none',
            !isEnabled &&
              conditionalEditCheck &&
              conditionalEditStyle === 'icon' &&
              'pr-0',
          )}
          type="text"
          name={htmlName !== undefined ? undefined : name}
          placeholder={placeholder}
          disabled={disabled || !isEnabled}
          id={effectiveId}
          defaultValue={defaultValue}
          maxLength={maxLength}
          autoComplete={autoComplete}
          ref={customRef as MutableRefObject<HTMLInputElement>}
          onChange={onInputChange}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(event)
          }}
        />
        {trailingIcon && (
          <Icon
            icon={trailingIcon}
            className={clsx(
              'mr-4 ml-4 [&_+_*]:pl-0 flex-shrink-0 w-4 h-4',
              trailingIconClassName,
            )}
          />
        )}
        {willShowEditIcon ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsEnabled(true)}
            className="w-4 h-4 ml-2 mr-4 disabled:text-neutral-600"
          >
            <Icon icon="edit" className="w-full h-full" />
          </button>
        ) : null}
      </div>
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function TextInput({
  name,
  control,
  rules,
  defaultValue,
  onChange,
  onBlur,
  disabled,
  shouldUnregister,
  ...props
}: TextInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }

        const onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
          onBlur?.(event)
        }
        return (
          <TextInputStandalone
            {...props}
            {...field}
            disabled={disabled}
            onChange={onInputChange}
            onBlur={onInputBlur}
            defaultValue={defaultValue}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
